import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PhoneOutgoing, Check } from "lucide-react";

type Vm = { id: string; callerName?: string; callerPhone?: string; transcript?: string; status: string; createdAt?: string; durationSec?: number };

export default function VoicemailInbox() {
  const list = useQuery<Vm[]>({ queryKey: ["/api/voicemails"] });
  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => apiRequest("PATCH", `/api/voicemails/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/voicemails"] }),
  });
  const callback = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/voicemails/${id}/create-callback`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/callback-requests"] }),
  });
  const data = list.data || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="page-voicemail">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Voicemail Inbox</h1>
        <p className="text-sm text-muted-foreground">{data.length} message(s)</p>
      </div>
      <div className="space-y-2">
        {data.map(v => (
          <Card key={v.id} data-testid={`card-vm-${v.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{v.callerName || v.callerPhone}</div>
                  <div className="text-xs text-muted-foreground">{v.createdAt && new Date(v.createdAt).toLocaleString()} · {v.durationSec || 0}s</div>
                </div>
                <Badge variant={v.status === "new" ? "default" : "outline"}>{v.status}</Badge>
              </div>
              {v.transcript && <p className="text-sm bg-muted/40 rounded p-2">{v.transcript}</p>}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: v.id, status: "heard" })} data-testid={`button-mark-heard-${v.id}`}>
                  <Check className="w-3 h-3 mr-1" />Mark heard
                </Button>
                <Button size="sm" onClick={() => callback.mutate(v.id)} data-testid={`button-callback-${v.id}`}>
                  <PhoneOutgoing className="w-3 h-3 mr-1" />Create callback
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {data.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">No voicemails.</p>}
      </div>
    </div>
  );
}
